/**
 * Shared Job Validation Module
 *
 * This module provides centralized validation logic for job imports across both:
 * - ImportTable.tsx (bulk import with programmatic validation)
 * - ImportForm_Manual.tsx (single entry with react-hook-form)
 *
 * All validation rules, error messages, and business logic are consolidated here
 * to ensure consistency across the application.
 */

import { AuthUser } from "../api/common";
import { Company, Deal, Job } from "../api";
import { DefaultImportTableColumns, ImportTableColumn } from "./types";
import { CREATE_NEW_SALESPERSON } from "./constants";
import { isValidPhoneNumber } from "libphonenumber-js";
import { IntegrationType } from "../components/submitjobs/ImportTable";
import { columnDisplayNames } from "../components/admin/ImportSettings/ImportSettings.types";

// #region DEFINITIONS

const regex = {
	positiveInteger: /^[1-9][0-9]*$/, // Positive integers only (>0)
	positiveDecimal: /^(?!(?:0+)(?:\.0+)?$)\d+(\.\d+)?$/, // Positive decimals only (>0), disallows 0 or 0.0; allows 0.1, 05.5, 5, 5.123
	email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

const validationPatterns = {
	frequencyOfMicros: regex.positiveInteger,
	depth: regex.positiveDecimal,
	density: regex.positiveDecimal,
};

const invalidMessage = {
	number: "Invalid number",
	phone: "Invalid phone number",
	email: "Invalid email address",
};

export const ValidationMessages: Record<ImportTableColumn, { required?: string; invalid?: string }> = {
	grower: {
		required: "Grower Name is required",
	},
	farm: {
		required: "Farm Name is required",
	},
	field: {
		required: "Field name is required",
	},
	boundaryGeoJSON: {
		required: "Boundary File is required",
	},
	pointsGeoJSON: {
		required: "Points File is required",
	},
	zonesGeoJSON: {
		required: "Zone File is required",
	},
	isReadyToSample: {
		required: "Ready to Sample is required",
	},
	deal: {
		required: "Deal Configuration is required",
	},
	density: {
		required: "Density is required",
		invalid: invalidMessage.number,
	},
	depth: {
		required: "Depth is required",
		invalid: invalidMessage.number,
	},
	testPackage: {
		required: "Test Package is required",
	},
	frequencyOfMicros: {
		required: "Frequency of Micros is required",
		invalid: invalidMessage.number,
	},
	fieldPriority: {
		required: "Field priority is required",
	},
	labSubmissionCode: {
		required: "Lab Submission Code is required",
	},
	growerPhoneNumber: {
		required: "Grower phone number is required",
		invalid: invalidMessage.phone,
	},
	submissionNotes: {
		required: "Submission Notes are required",
	},
	branch: {
		required: "Branch is required",
	},
	fieldId: {
		required: "Field ID is required",
	},
	lastCrop: {
		required: "Last crop is required",
	},
	nextCrop: {
		required: "Next crop is required",
	},
	yieldGoal: {
		required: "Yield Goal is required",
	},
	dateFieldReadyEstimated: {
		required: "Estimated field ready date is required",
	},
	labInstructions: {
		required: "Lab Instructions are required",
	},
	salesperson: {
		required: "Salesperson is required",
	},
	salespersonMobilePhone: {
		required: "Salesperson phone number is required",
		invalid: invalidMessage.phone,
	},
	submitterName: {
		required: "Submitter Name is required",
	},
	submitterEmail: {
		required: "Submitter Email is required",
		invalid: invalidMessage.email,
	},
	eventId: {
		required: "Event/Sync Code is required",
	},
	collectBoundary: {
		required: "Collect Boundary is required",
	},
	isTestJob: {
		required: "Test Job flag is required",
	},
	microsList: {
		required: "Sample Test Specifier is required",
	},
	fieldNotes: {
		required: "Field Notes are required",
	},
	labName: {
		required: "Lab Name is required",
	},
	externalLabId: {
		required: "External Lab ID is required",
	},
	externalId: {
		required: "External ID is required",
	},
	externalFarmId: {
		required: "External Farm ID is required",
	},
};

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// ==========================================================================================
type FieldProperty = keyof Job | keyof Job["files"] | "salespersonMobilePhone";
export const FIELD_TO_PROPERTY_MAP: Record<ImportTableColumn, FieldProperty> = {
	grower: "importGrowerName",
	farm: "importFarmName",
	field: "importFieldName",
	boundaryGeoJSON: "boundaryGeoJSON",
	pointsGeoJSON: "pointsGeoJSON",
	zonesGeoJSON: "zonesGeoJSON",
	isReadyToSample: "isReadyToSample",
	deal: "dealIds",
	density: "importDensity",
	depth: "importDepth",
	testPackage: "externalTestPackageId",
	frequencyOfMicros: "externalFrequencyOfMicros",
	fieldPriority: "fieldPriority",
	labSubmissionCode: "labSubmissionCode",
	growerPhoneNumber: "growerPhoneNumber",
	submissionNotes: "submissionNotes",
	branch: "branchIds",
	fieldId: "fieldId",
	lastCrop: "lastCrop",
	nextCrop: "nextCrop",
	yieldGoal: "yieldGoal",
	dateFieldReadyEstimated: "dateFieldReadyEstimated",
	labInstructions: "labInstructions",
	salesperson: "salespersonIds",
	salespersonMobilePhone: "salespersonMobilePhone",
	submitterName: "submitterName",
	submitterEmail: "submitterEmail",
	eventId: "eventId",
	collectBoundary: "collectBoundary",
	isTestJob: "isTestJob",
	microsList: "microsList",
	fieldNotes: "fieldNotes",
	labName: "labName",
	externalLabId: "externalLabId",
	externalId: "externalId",
	externalFarmId: "externalFarmId",
};

export interface ValidationContext {
	/** Company configuration (drives base field visibility/requirements) */
	company?: Company;

	/**
	 * Deal configuration (drives deal-specific validation rules)
	 * - If present: Applies deal-specific logic (eventId, labSubmissionCode, etc.)
	 * - If absent: Falls back to base company requirements
	 */
	deal?: Deal;

	/** All available deals (used for deal lookup during batch validation) */
	allDeals?: Deal[];

	/** Current job being validated (used for context-specific rules) */
	job?: Job;

	/** Current user (used for visibility rules) */
	user?: AuthUser;

	/** Whether a points file is attached (affects field visibility) */
	hasPointsFile?: boolean;

	/** Whether creating a new salesperson (affects salespersonMobilePhone requirement) */
	isCreatingNewSalesperson?: boolean;

	/** Whether user intends to attach points (affects file field requirements) */
	isPointsAttached?: boolean;

	/** Proagrica, Agrian, etc */
	integration?: IntegrationType;
}

export interface ValidationResult {
	isValid: boolean;
	error?: string;
}
// #endregion

// #region HELPERS

/** Check if a field is required based on company settings and business logic */
export function isFieldRequired(fieldType: ImportTableColumn, context: ValidationContext): boolean {
	const company = context.company;

	// Get base required status from company settings or defaults
	let isRequiredPerCompany = false;
	if (company?.portalSettings?.importTableColumns?.[fieldType]) {
		isRequiredPerCompany = company.portalSettings.importTableColumns[fieldType].required;
	} else {
		isRequiredPerCompany = DefaultImportTableColumns[fieldType]?.required ?? false;
	}

	// Apply business logic for special fields
	switch (fieldType) {
		case "eventId": {
			if (context.integration == "Agrian") return false;
			const filesRequired =
				isFieldRequired("boundaryGeoJSON", { company }) ||
				isFieldRequired("pointsGeoJSON", { company }) ||
				isFieldRequired("zonesGeoJSON", { company });
			return isRequiredPerCompany || (filesRequired && syncMethodContains("event", context));
		}

		case "labSubmissionCode":
			return isRequiredPerCompany || syncMethodContains("lab", context);

		case "fieldPriority": {
			if (isFieldVisible("isReadyToSample", context)) {
				return isRequiredPerCompany && (context.job?.isReadyToSample ?? false);
			} else {
				return isRequiredPerCompany;
			}
		}

		case "microsList":
			return isRequiredPerCompany && (context.hasPointsFile ?? false);

		case "zonesGeoJSON":
			return (
				isRequiredPerCompany ||
				(context.deal?.type === "Zone" && !context.deal?.isComposite && !context.deal?.isMultiComposite)
			);

		case "pointsGeoJSON": {
			return (
				isRequiredPerCompany ||
				!!context.isPointsAttached ||
				!!context.deal?.isPtsRequired ||
				(syncMethodContains("immut", context) && context.deal?.type === "Grid" && context.integration != "Agrian") ||
				context.deal?.type === "ModGrid"
			);
		}

		case "salespersonMobilePhone":
			return isRequiredPerCompany && (context.isCreatingNewSalesperson ?? false);

		case "branch": {
			const hasBranches = (context.company?.branchIds?.length ?? 0) > 0;
			const isLabBranch = (context.deal?.labAccountNumber ?? "").toLowerCase().includes("branch");
			return isRequiredPerCompany || hasBranches || isLabBranch;
		}

		default:
			return isRequiredPerCompany;
	}
}

function syncMethodContains(value: string, context: ValidationContext) {
	const syncMethod1 = context.deal?.syncMethod?.toString().toLowerCase() ?? "";
	const syncMethod2 = context.deal?.syncMethod2?.toString().toLowerCase() ?? "";
	return syncMethod1.includes(value) || syncMethod2.includes(value);
}

/** Check if a field is visible based on company settings and business logic */
export function isFieldVisible(fieldType: ImportTableColumn, context: ValidationContext): boolean {
	const company = context.company;

	// Get base visibility from company settings or defaults
	let isVisiblePerCompany = true;
	if (company?.portalSettings?.importTableColumns?.[fieldType]) {
		isVisiblePerCompany = company.portalSettings.importTableColumns[fieldType].visible;
	} else {
		isVisiblePerCompany = DefaultImportTableColumns[fieldType]?.visible ?? true;
	}

	// Even if not explicitly visible, required fields must be shown
	const isVisible = isVisiblePerCompany || isFieldRequired(fieldType, context);

	// Apply business logic for special fields
	switch (fieldType) {
		case "eventId":
			return isVisible || syncMethodContains("event", context);

		case "fieldPriority":
			return isVisible || isFieldVisible("isReadyToSample", context);

		case "microsList":
			return isFieldRequired(fieldType, context) || (isVisiblePerCompany && (context.hasPointsFile ?? false));

		case "isTestJob":
			return isVisible || (context.user?.isSuperUser ?? false);

		default:
			return isVisible;
	}
}

/** Check if a value is considered empty for a given field type */
export function isEmpty(value: any, fieldType: ImportTableColumn): boolean {
	if (value === undefined || value === null) {
		return true;
	} else if (isBooleanField(fieldType)) {
		// For boolean fields, false is a valid value, only undefined/null is empty
		return false;
	} else if (isNumericField(fieldType)) {
		return value === "";
	} else if (isArrayField(fieldType)) {
		return Array.isArray(value) && value.length === 0;
	} else if (typeof value === "string") {
		return value.trim() === "";
	} else {
		return false;
	}
}

export function isArrayField(fieldType: ImportTableColumn): boolean {
	return ["salesperson", "branch", "deal"].includes(fieldType);
}

export function isBooleanField(fieldType: ImportTableColumn): boolean {
	return ["isReadyToSample", "collectBoundary", "isTestJob"].includes(fieldType);
}

export function isNumericField(key: ImportTableColumn): boolean {
	return ["density", "depth", "frequencyOfMicros"].includes(key);
}
// #endregion

// #region VALIDATION FUNCTIONS

/**
 * Validates a single field value based on its type and context
 * This is the main validation function used by both components
 */
export function validateField(fieldType: ImportTableColumn, value: any, context: ValidationContext): ValidationResult {
	// Boolean fields are always valid - they have only two states (true/false), both valid
	if (isBooleanField(fieldType)) {
		return { isValid: true };
	}

	const isRequired = isFieldRequired(fieldType, context);

	// If not required and no value, it's valid
	if (!isRequired && isEmpty(value, fieldType)) {
		return { isValid: true };
	}

	// Required field check
	if (isRequired && isEmpty(value, fieldType)) {
		return {
			isValid: false,
			error: ValidationMessages[fieldType]?.required || `${columnDisplayNames[fieldType]} is required`,
		};
	}

	// Field-specific validation
	switch (fieldType) {
		case "frequencyOfMicros":
		case "depth":
		case "density":
			return validateNumeric(value, fieldType);

		case "growerPhoneNumber":
		case "salespersonMobilePhone":
			return validatePhone(value, context, fieldType);

		case "submitterEmail":
			return validateEmail(value, fieldType);

		case "salesperson":
			return validateSalesperson(value, context.job);

		default:
			// For all other fields, if they're required and we got here,
			// the isEmpty check above handles validation
			return { isValid: true };
	}
}

/** Validate numeric fields (density, depth, frequencyOfMicros) */
function validateNumeric(value: any, fieldType: ImportTableColumn): ValidationResult {
	const pattern = validationPatterns[fieldType as keyof typeof validationPatterns] || /.*/;
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid number";

	// Check for empty values (but allow 0)
	if (value === undefined || value === null || value === "") {
		return { isValid: false, error: invalidMessage };
	}

	// Convert to number and check if valid
	const numValue = Number(value);
	if (isNaN(numValue)) {
		return { isValid: false, error: invalidMessage };
	}

	// Check pattern (validates positive numbers, decimals, etc.)
	const stringValue = value.toString();
	if (!pattern.test(stringValue)) {
		return { isValid: false, error: invalidMessage };
	}

	return { isValid: true };
}

/** Validate phone numbers */
function validatePhone(
	value: any,
	context: ValidationContext,
	fieldType: "growerPhoneNumber" | "salespersonMobilePhone",
): ValidationResult {
	const isRequired = isFieldRequired(fieldType, context);
	const requiredMessage = ValidationMessages[fieldType].required || "Phone number is required";
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid phone number";

	if (!isRequired && isEmpty(value, fieldType)) {
		return { isValid: true };
	}

	if (isRequired && isEmpty(value, fieldType)) {
		return {
			isValid: false,
			error: requiredMessage,
		};
	}

	// If we have a value, validate the phone format
	if (value) {
		const phoneString = value.toString();
		const valid = isValidPhoneNumber(phoneString, "US");

		if (!valid) {
			return {
				isValid: false,
				error: invalidMessage,
			};
		}
	}

	return { isValid: true };
}

/** Validate email addresses */
function validateEmail(value: any, fieldType: ImportTableColumn): ValidationResult {
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid email address";

	if (value && typeof value === "string") {
		const isValid = regex.email.test(value);

		if (!isValid) {
			return {
				isValid: false,
				error: invalidMessage,
			};
		}
	}

	return { isValid: true };
}

/** Validate salesperson (including new salesperson creation) */
function validateSalesperson(value: any, job?: Job): ValidationResult {
	// Note: Empty array check is handled by isEmpty() in validateField()
	// This function only validates the content when a value is present

	// Check for new salesperson validation
	if (job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON)) {
		if (!job.newSalesperson?.name || job.newSalesperson.name.trim() === "") {
			return { isValid: false, error: "New salesperson name is required" };
		}
	}

	return { isValid: true };
}

const FileTypes = ["boundaryGeoJSON", "pointsGeoJSON", "zonesGeoJSON", "fieldNotes"] as const;
export type FileType = (typeof FileTypes)[number];

/** Check if a job has GeoJSON data for a given file type */
export function hasGeoJSON(job: Job, fileType: FileType): boolean {
	if (fileType === "fieldNotes") return false;
	const geojson = job.files?.[fileType] as GeoJSON.FeatureCollection | undefined;
	return !!geojson && Array.isArray(geojson.features) && geojson.features.length > 0;
}

/** Validate file upload fields */
export function validateFile(fileType: FileType, job: Job, context: ValidationContext): ValidationResult {
	const isRequired = isFieldRequired(fileType as ImportTableColumn, context);

	if (!isRequired) {
		return { isValid: true };
	}

	if (!hasGeoJSON(job, fileType)) {
		return {
			isValid: false,
			error: `${columnDisplayNames[fileType]} required${fileType === "zonesGeoJSON" ? " for Zone deals" : ""}`,
		};
	}

	return { isValid: true };
}

/**
 * Validate point file type (cores vs centroids) for deal type
 * Zone deals should use cores, Grid/ModGrid deals should use centroids
 */
export function validatePointFileType(job: Job, deal?: Deal): ValidationResult {
	// Check if we have points GeoJSON
	const hasPoints = hasGeoJSON(job, "pointsGeoJSON");
	if (!deal || !hasPoints) {
		return { isValid: true };
	}

	// Check fileOptions to determine if points are cores or centroids
	const coresGroup = job.fileOptions?.pointsOptions?.find((g) => g.type.type === "Cores");
	const centroidsGroup = job.fileOptions?.pointsOptions?.find((g) => g.type.type === "Centroids");

	// Check which group the selected GeoJSON came from
	const selectedGeoJSON = job.files.pointsGeoJSON;
	if (!selectedGeoJSON) {
		// No specific file selected, can't determine source
		return { isValid: true };
	}

	const isFromCores = coresGroup?.options?.some((opt) => opt.geoJSON === selectedGeoJSON);
	const isFromCentroids = centroidsGroup?.options?.some((opt) => opt.geoJSON === selectedGeoJSON);

	// Zone deals should use cores
	if (deal.type === "Zone" && isFromCentroids && !isFromCores) {
		return {
			isValid: false,
			error: "Zone deals use cores, not centroids",
		};
	}

	// Grid/ModGrid deals should use centroids
	if ((deal.type === "Grid" || deal.type === "ModGrid") && isFromCores && !isFromCentroids) {
		return {
			isValid: false,
			error: "Grid deals use centroids, not cores",
		};
	}

	return { isValid: true };
}

// #endregion

// #region BATCH VALIDATION (for ImportTable)

/**
 * Creates a standardized validation error key
 * @param fieldType - The field/column identifier
 * @param jobId - The job ID
 * @returns Error key in format: `${fieldType}-${jobId}`
 *
 * @example
 * getValidationErrorKey("density", "job-123") // "density-job-123"
 * getValidationErrorKey("grower", "job-456") // "grower-job-456"
 */
export function getValidationErrorKey(fieldType: ImportTableColumn, jobId: string): string {
	return `${fieldType}-${jobId}`;
}

/**
 * Parses a validation error key to extract the field type
 * @param errorKey - The error key in format `${fieldType}-${jobId}`
 * @returns The field type, or undefined if the key is malformed
 *
 * @example
 * parseValidationErrorKey("density-job-123") // "density"
 * parseValidationErrorKey("grower-job-456") // "grower"
 */
export function parseValidationErrorKey(errorKey: string): ImportTableColumn | undefined {
	if (!errorKey.includes("-")) {
		return undefined;
	}
	const parts = errorKey.split("-");
	if (parts.length < 2) {
		return undefined;
	}
	return parts[0] as ImportTableColumn;
}

/**
 * Validate multiple jobs at once
 * Returns an object with errors keyed by `${fieldType}-${jobId}`
 * This is used by ImportTable.tsx for bulk validation
 */
export function validateJobs(jobs: Job[], context: ValidationContext): Record<string, string> {
	const errors: Record<string, string> = {};

	for (const job of jobs) {
		function errorKey(fieldType: ImportTableColumn): string {
			return getValidationErrorKey(fieldType, job.id);
		}
		const isCreatingNewSalesperson = job.salespersonIds?.includes(CREATE_NEW_SALESPERSON) ?? false;

		const deal: Deal | undefined = context.allDeals?.find((d) => d.id === job.dealIds?.[0]);
		const jobContext: ValidationContext = {
			...context,
			job,
			deal,
			isCreatingNewSalesperson,
		};

		// Validate all standard fields
		Object.keys(FIELD_TO_PROPERTY_MAP).forEach((fieldTypeKey) => {
			const fieldType = fieldTypeKey as ImportTableColumn;

			// Skip file fields (validated separately)
			if (FileTypes.includes(fieldType as FileType)) {
				return;
			}

			const propertyName = FIELD_TO_PROPERTY_MAP[fieldType];

			// Get the value from the correct location
			let value: any;
			if (fieldType === "salespersonMobilePhone") {
				// Special case: nested in newSalesperson object
				value = job.newSalesperson?.phone;
			} else {
				// Standard case: use property map
				value = (job as any)[propertyName];
			}

			const result = validateField(fieldType, value, jobContext);

			if (!result.isValid) {
				errors[errorKey(fieldType)] = result.error!;
			}
		});

		const fileContext: ValidationContext = {
			...jobContext,
			// jobType: deal?.type,
			isPointsAttached: job.isPointsAttached,
		};

		FileTypes.forEach((fileType) => {
			const result = validateFile(fileType, job, fileContext);
			if (!result.isValid) {
				errors[`${fileType}-${job.id}`] = result.error!;
			}
		});

		// Validate point file type
		if (deal) {
			const pointTypeResult = validatePointFileType(job, deal);
			if (!pointTypeResult.isValid) {
				errors[errorKey("pointsGeoJSON")] = pointTypeResult.error!;
			}
		}
	}

	return errors;
}

// #endregion

// #region REACT-HOOK-FORM INTEGRATION (for ImportForm_Manual)

/**
 * Get validation rules for react-hook-form's register() function
 * This converts our validation logic to react-hook-form's declarative format
 */
export function getFormValidationRules(
	fieldType: ImportTableColumn,
	context: ValidationContext,
): {
	required: string | boolean;
	pattern?: { value: RegExp; message: string };
	validate?: (value: any) => string | boolean;
} {
	const isRequired = isFieldRequired(fieldType, context);

	const rules: any = {
		required: isRequired
			? ValidationMessages[fieldType]?.required || `${columnDisplayNames[fieldType]} is required`
			: false,
	};

	// For fields with custom validation beyond "required", delegate to validateField
	const fieldsWithCustomValidation = [
		"growerPhoneNumber",
		"salespersonMobilePhone",
		"frequencyOfMicros",
		"depth",
		"density",
		"submitterEmail",
	];

	if (fieldsWithCustomValidation.includes(fieldType)) {
		rules.validate = (value: any) => {
			// Skip validation if empty and not required (let "required" rule handle it)
			if (!value && value !== 0) {
				return true;
			}

			// Delegate to centralized validation
			const result = validateField(fieldType, value, context);
			return result.isValid ? true : result.error!;
		};
	}

	return rules;
}
